// Frontend usage tracker. Batches two event types, POSTs to /api/usage/ (backend
// stamps identity from the JWT - client never sends it). Mounted once via
// useUsageTracker() in the app shell.
//   - feature_heartbeat: every HEARTBEAT_MS while focused/visible/not-idle (active time)
//   - ui_interaction: trackTool(name, detail) or delegated data-track autocapture

const HEARTBEAT_MS = 15000; // cadence is owned here; sent per-event as heartbeat_s
const FLUSH_MS = 15000;
const IDLE_MS = 60000;
const MAX_QUEUE = 50;
const ENDPOINT = '/api/usage/'; // same-origin; ingress routes /api/* to Django

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'wheel', 'touchstart', 'click'];

// Discriminated union: heartbeat_s only on heartbeats, tool/detail only on interactions.
type BaseEvent = { feature: string | null; sub_feature: string | null; ts: number };
type HeartbeatEvent = BaseEvent & { event: 'feature_heartbeat'; heartbeat_s: number };
type InteractionEvent = BaseEvent & { event: 'ui_interaction'; tool: string; detail?: string };
type UsageEvent = HeartbeatEvent | InteractionEvent;

function newSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

class UsageTracker {
    private started = false;
    private feature = 'Other';
    private subFeature: string | null = null;
    private sessionId: string | null = null;
    private queue: UsageEvent[] = [];
    private lastActivity = 0;
    private focused = true;
    private hbTimer?: ReturnType<typeof setInterval>;
    private flushTimer?: ReturnType<typeof setInterval>;
    private failCount = 0; // consecutive ingest failures, for throttled warnings

    // Bound handlers (stable references so add/removeEventListener match).
    private onActivity = (): void => {
        this.lastActivity = Date.now();
    };
    private onFocus = (): void => {
        this.focused = true;
        this.lastActivity = Date.now();
    };
    private onBlur = (): void => {
        this.focused = false;
    };
    private onVisibility = (): void => {
        if (document.hidden) this.flush(true);
        else this.lastActivity = Date.now();
    };
    private onPageHide = (): void => this.flush(true);

    // Delegated autocapture: elements with [data-track] emit a ui_interaction on
    // click/submit (capture phase, so stopPropagation can't hide it). Only the
    // developer-set data-track value is read (never element text) - keep it PHI-free.
    private onDelegated = (e: Event): void => {
        const target = e.target as Element | null;
        const el = target?.closest?.('[data-track]') as HTMLElement | null;
        const name = el?.dataset.track;
        if (name) this.trackTool(name, el?.dataset.trackDetail);
    };

    start(): void {
        if (this.started || typeof window === 'undefined') return;
        this.started = true;
        this.sessionId = newSessionId();
        this.lastActivity = Date.now();
        this.focused = typeof document !== 'undefined' ? !document.hidden : true;

        ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, this.onActivity, { passive: true }));
        window.addEventListener('focus', this.onFocus);
        window.addEventListener('blur', this.onBlur);
        document.addEventListener('visibilitychange', this.onVisibility);
        window.addEventListener('pagehide', this.onPageHide);
        window.addEventListener('click', this.onDelegated, true);
        window.addEventListener('submit', this.onDelegated, true);

        this.hbTimer = setInterval(() => this.heartbeat(), HEARTBEAT_MS);
        this.flushTimer = setInterval(() => this.flush(false), FLUSH_MS);
    }

    stop(): void {
        if (!this.started) return;
        this.started = false;
        clearInterval(this.hbTimer);
        clearInterval(this.flushTimer);
        ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, this.onActivity));
        window.removeEventListener('focus', this.onFocus);
        window.removeEventListener('blur', this.onBlur);
        document.removeEventListener('visibilitychange', this.onVisibility);
        window.removeEventListener('pagehide', this.onPageHide);
        window.removeEventListener('click', this.onDelegated, true);
        window.removeEventListener('submit', this.onDelegated, true);
        this.flush(true);
    }

    setFeature(feature: string | null): void {
        if (feature) this.feature = feature;
        this.subFeature = null; // new page: reset until URL/UsageSection sets it
    }

    // Within-page section: from the URL (tabbed pages) or a UsageSection (hover/focus).
    setSubFeature(subFeature: string | null): void {
        this.subFeature = subFeature || null;
    }

    trackTool(tool: string, detail?: string): void {
        if (!tool) return;
        this.enqueue({
            event: 'ui_interaction',
            tool,
            feature: this.feature,
            sub_feature: this.subFeature,
            detail,
            ts: Date.now(),
        });
    }

    private isActive(): boolean {
        const visible = typeof document === 'undefined' || !document.hidden;
        return this.focused && visible && Date.now() - this.lastActivity < IDLE_MS;
    }

    private heartbeat(): void {
        if (this.isActive())
            this.enqueue({
                event: 'feature_heartbeat',
                feature: this.feature,
                sub_feature: this.subFeature,
                heartbeat_s: HEARTBEAT_MS / 1000,
                ts: Date.now(),
            });
    }

    private enqueue(event: UsageEvent): void {
        this.queue.push(event);
        if (this.queue.length >= MAX_QUEUE) this.flush(false);
    }

    private flush(keepalive: boolean): void {
        if (!this.queue.length) return;
        // Token is JSON-encoded in localStorage (see aceContext.ts); parse it or the
        // Bearer carries quotes. No token = not logged in yet (silent); unparseable = warn.
        let token: string | null = null;
        const raw = window.localStorage?.getItem('accessToken');
        if (raw) {
            try {
                token = JSON.parse(raw);
            } catch {
                console.warn('[usage] could not parse accessToken; events not sent');
            }
        }
        const events = this.queue;
        this.queue = [];
        if (!token) return; // not authenticated yet; drop quietly
        try {
            fetch(ENDPOINT, {
                method: 'POST',
                keepalive,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ session_id: this.sessionId, events }),
            })
                .then((r) => {
                    // fetch doesn't reject on HTTP errors; check status so a broken
                    // pipeline (401/404/500) is visible, not silently lost.
                    if (r.ok) {
                        if (this.failCount) {
                            console.warn('[usage] ingest recovered');
                            this.failCount = 0;
                        }
                    } else if (this.failCount++ === 0) {
                        console.warn(`[usage] ingest failing: HTTP ${r.status}`);
                    }
                })
                .catch((e) => {
                    if (this.failCount++ === 0) console.warn('[usage] ingest network error', e);
                });
        } catch (e) {
            // never break the app, but surface the bug
            console.warn('[usage] flush threw', e);
        }
    }
}

const tracker = new UsageTracker();

export function trackTool(tool: string, detail?: string): void {
    tracker.trackTool(tool, detail);
}

// Mark the current within-page sub-feature (e.g. from an onPointerEnter handler).
export function setSubFeature(name: string | null): void {
    tracker.setSubFeature(name);
}

export default tracker;
